/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   DependencyMap.tsx
 *     Feature         :   117-operational-dashboards
 *     Description     :   Hierarchical dependency tree visualization
 *     Constitution    :   v1.12.0 | Enterprise Dependency Architecture
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import React, { useMemo } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import type { DependencyNode, DependencyEdge } from '../../services/operations/slaService';

interface DependencyMapProps {
  nodes: DependencyNode[];
  edges: DependencyEdge[];
  impactedNodes?: string[];
}

const TYPE_COLORS: Record<string, string> = {
  Lakehouse: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Warehouse: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  Workspace: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  Capacity: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

interface TreeNode {
  node: DependencyNode;
  children: TreeNode[];
}

export const DependencyMap: React.FC<DependencyMapProps> = ({ nodes, edges, impactedNodes = [] }) => {
  const impactedSet = useMemo(() => new Set(impactedNodes), [impactedNodes]);

  const tree = useMemo(() => {
    // Find root nodes (those that are sources but not targets, or Type 1/4)
    const targetSet = new Set(edges.map(e => e.target));
    const roots = nodes.filter(n =>
      !targetSet.has(n.id) || n.capacity_type === 'Platform' || n.capacity_type === 'Reference'
    );

    const buildTree = (nodeId: string, visited: Set<string>): TreeNode | null => {
      if (visited.has(nodeId)) return null;
      visited.add(nodeId);
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return null;
      const childEdges = edges.filter(e => e.source === nodeId);
      const children = childEdges
        .map(e => buildTree(e.target, visited))
        .filter((c): c is TreeNode => c !== null);
      return { node, children };
    };

    return roots.map(r => buildTree(r.id, new Set())).filter((t): t is TreeNode => t !== null);
  }, [nodes, edges]);

  const renderNode = (treeNode: TreeNode, depth: number = 0): React.ReactNode => {
    const { node, children } = treeNode;
    const isImpacted = impactedSet.has(node.id);

    return (
      <div key={node.id} className={clsx('ml-4', depth === 0 && 'ml-0')}>
        <div className={clsx(
          'flex items-center gap-2 py-2 px-3 rounded-md mb-1',
          isImpacted ? 'bg-red-50 dark:bg-red-950 border border-red-200' : 'hover:bg-[var(--color-bg-subtle)]'
        )}>
          {children.length > 0 && <ChevronRight className="h-4 w-4 text-[var(--color-text-secondary)]" />}
          <span className={clsx('inline-flex px-2 py-0.5 rounded text-xs font-medium', TYPE_COLORS[node.capacity_type] ?? 'bg-gray-100 text-gray-800')}>
            {node.capacity_type}
          </span>
          <span className="text-sm font-medium text-[var(--color-text-primary)]">{node.capacity_name}</span>
          {node.workspace_count > 0 && (
            <span className="text-xs text-[var(--color-text-secondary)]">{node.workspace_count} consumers</span>
          )}
          <span className={clsx('text-xs font-medium', node.status === 'Healthy' ? 'text-green-600' : node.status === 'Warning' ? 'text-yellow-600' : 'text-red-600')}>
            {node.status}
          </span>
        </div>
        {children.length > 0 && (
          <div className="border-l-2 border-[var(--color-border)] ml-3">
            {children.map(c => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (tree.length === 0) {
    return <div className="text-[var(--color-text-secondary)] py-4 text-center">No dependency tree available</div>;
  }

  return <div className="space-y-1">{tree.map(t => renderNode(t))}</div>;
};
